/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 12.05.17.
 */
import alt from '../alt';
import _ from 'underscore';
import url from "url";

import CONFIG from "../../config";
import AppServices from "../../AppServices";
import {RestAction, GetRestAction, PutRestAction, PostRestAction} from "../RestAction";
import BaseRestStore from '../BaseRestStore';
import SessionActions from './SessionActions';
import Log from "../../Log";
import SchemaRegistry from "../SchemaRegistry";
import {queryParse} from "../../util";

/**
 * @typedef {Object} SessionState
 * @property {Rights} rights Access rights
 * @property {string} idFieldName ID field name
 * @property {boolean} isLoading If the data exchange is currently in progress
 * @property {RestAction} action Currently running action name or if the value is an array then action name with parameters
 * @property {RestAction[]} actionStack Active actions stack
 * @property {RestApiErrorResponse} error Last command error object in case when last command failed
 * @property {Session[]} data Loaded list data (contains only records for current page!)
 * @property {Session} currentRecord Loaded current record data
 * @property {number} recordCount Total number of rows currently requested
 * @property {number} pageSize Max record count in page (0 if no pages)
 * @property {number} firstRecord First record number in list data (0-based)
 * @property {string[]} sort Sort fields as an array. Prefix sort field name with "-" to get descending sort for this field. Default is ["+id"]
 * @property {Object} filter Filter - object of field names and values
 * @property {string} nonce Nonce
 * @property {string} passwordTab Active password window tab
 */
/**
 * @typedef {Object} Rights
 * @property {?Object} GET
 * @property {?Object} POST
 * @property {?Object} PUT
 * @property {?Object} DELETE
 */
/**
 * @typedef {Object} Session
 * @property {AccessRightCollection} accessRightCollection Access Rights Collection
 * @property {PersonRole} activeRole Active PersonRole object
 * @property {Application} application Application object
 * @property {string} authType Authentication type
 * @property {string} created Session creation timestamp
 * @property {number} id Session ID
 * @property {string} ip IP address
 * @property {Person} person Person object
 * @property {string} status Status
 * @property {string} token Access token
 * @property {string} validTo Access token validity timestamp
 */
/**
 * @typedef {Object} AccessRightCollection
 * @property {AccessRight[]} items
 */
/**
 * @typedef {Object} AccessRight
 * @property {string} resourceMethods
 * @property {string} resourcePath
 */
/**
 * @typedef {Object} Application
 * @property {string} clientId Client ID
 * @property {string} code Code
 * @property {string} name Name
 * @property {string} redirectUrl Redirect URL
 * @property {bool} askRole Force role selection
 */
/**
 * @typedef {Object} Person
 * @property {string} clientId Client ID
 * @property {string} email E-mail
 * @property {string} firstName First name
 * @property {string} lastName Last name
 * @property {number} id Person ID
 * @property {bool} requirePasswordChange
 * @property {PersonRoleCollection} personRoleCollection
 */
/**
 * @typedef {Object} PersonRoleCollection
 * @property {PersonRole[]} items
 */
/**
 * @typedef {Object} PersonRole
 * @property {string} clientId
 * @property {number} id ID
 * @property {bool} isDefault Is default role
 * @property {Organization} organization Organization
 * @property {Role} role Role
 */
/**
 * @typedef {Object} Organization
 * @property {string} clientId Client ID
 * @property {number} id ID
 * @property {string} name Name
 */
/**
 * @typedef {Object} Role
 * @property {string} clientId Client ID
 * @property {number} id ID
 * @property {string} name Name
 */

 /*
 *  {
 *      application: {
 *          clientId: clientId,
 *          code: code,
 *          name: name,
 *          redirectUrl: redirectUrl
 *      },
 *      authType: authType,
 *      created: created,
 *      id: id,
 *      ip: ip,
 *      person: {
 *          clientId: clientId,
 *          email: email,
 *          firstName: firstName,
 *          id: id,
 *          lastName: lastName,
 *          requirePasswordChange: bool,
 *          personRoleCollection: {
 *              items: [
 *                  {
 *                      "@type": "personRole",
 *                      clientId: clientId,
 *                      id: id,
 *                      isDefault: isDefault,
 *                      organization: {
 *                          clientId: clientId,
 *                          id: id,
 *                          name: name
 *                      },
 *                      role: {
 *                          clientId: clientId,
 *                          description: description,
 *                          id: id,
 *                          name: name
 *                      }
 *                  }
 *              ]
 *          },
 *      },
 *      status: status,
 *      token: token,
 *      validTo: validTo
 *  }
 */
class SessionStore extends BaseRestStore {
    /**
     * @member {SessionState} state
     */

    /**
     *
     */
    constructor() {
        super(SessionActions, "session");
        this.exportPublicMethods({
            isAuthenticated: this.isAuthenticated,
            isAuthorized: this.isAuthorized,
            filterAuthorizedActions: this.filterAuthorizedActions,
            getToken: this.getToken,
            passwordChangeRequired: this.passwordChangeRequired,
            calcRights: this.calcRights,
            calcLinksRights: this.calcLinksRights
        });
        let storedSession = this.loadSession();
        _.extend(this.state, storedSession);
    }

    /**
     * @protected
     * @returns {*}
     */
    getClearState() {
        let state = super.getClearState();
        return _.extend(state, {
            nonce: null
        });
    }

    onSetPasswordTab(name) {
        this.saveState({passwordTab: name});
    }

    /**
     * @protected
     * @param authToken
     */
    onLogin(authToken) {
        this.onPost({
            "application": {
                "code": CONFIG.applicationCode,
                "redirectUrl": CONFIG.redirectUrl
            },
            "authToken": authToken
        });
    }

    /**
     * @protected
     */
    onLogout() {
        this.onSessionExpired();
        this.onDel("current");
    }

    onSessionExpired() {
        this.clearState();
        this.clearSession();
    }

    /**
     * @protected
     * @param nonce
     */
    onStartAuthentication(nonce) {
        let action = new RestAction("startAuthentication");
        action.state = "finished";
        let newState = this.getClearState();
        newState.nonce = nonce;
        newState.action = action;
        this.clearSession();
        AppServices.getRestApi().setAuthToken(null);
        this.saveState(newState);
    }

    /**
     * @protected
     * @param id
     */
    onGet(id) {
        this.onActionStart(new GetRestAction("current"));
    }

    /**
     * @protected
     * @param info
     */
    onPut(info) {
        this.onActionStart(new PutRestAction("current", info.row));
    }

    onChangePassword(data) {
        let action = new PostRestAction(data);
        action.authToken = "";
        this.onActionStart(action);
    }

    /**
     * @protected
     * @param state
     */
    storeSession(state) {
        if (state) {
            if (!_.has(state, "currentRecord")) state.currentRecord = this.state.currentRecord;
            if (!_.has(state, "nonce")) state.nonce = this.state.nonce;
            if (!_.has(state, "passwordTab")) state.passwordTab = this.state.passwordTab;
        }
        else state = this.state;
        this.saveSession(state);
    }

    /**
     * @protected
     */
    clearSession() {
        localStorage.removeItem('session');
    }

    /**
     * @protected
     * @returns {*}
     */
    loadSession() {
        return this.deserialize(localStorage.getItem('session'));
    }

    /**
     * @protected
     * @param state
     */
    saveSession(state) {
        localStorage.setItem('session', this.serialize(state));
    }

    /**
     * @protected
     */
    restoreSession() {
        let newState = this.loadSession();
        this.saveState(newState, true);
    }

    /**
     * @protected
     * @param state
     */
    serialize(state) {
        let savedData = {
            currentRecord: state.currentRecord,
            nonce: state.nonce,
            passwordTab: state.passwordTab
        };
        return JSON.stringify(savedData);
    }

    /**
     * @protected
     * @param data
     * @returns {*}
     */
    deserialize(data) {
        if (data == null) return null;
        let state = null;
        try {
            state = JSON.parse(data);
        } catch(e) {
            Log.error("Error parsing session data from localStorage");
            return null;
        }
        if (state) {
            let newState = {};
            if (state.currentRecord) newState.currentRecord = state.currentRecord;
            if (state.nonce) newState.nonce = state.nonce;
            if (state.passwordTab) newState.passwordTab = state.passwordTab;
            return newState;
        }
        return null;
    }

    /**
     * @protected
     * @param newValue
     * @param doNotStoreSession
     */
    saveState(newValue, doNotStoreSession = false) {
        if (!doNotStoreSession) this.storeSession(newValue);
        if (newValue.currentRecord)
            AppServices.getRestApi().setAuthToken( (newValue.currentRecord.token ? newValue.currentRecord.token : null) );
        newValue.rights = this.calcRights(newValue);
        super.saveState(newValue);
    }

    calcRights(state) {
        if (!state) state = this.state;
        if (state.currentRecord && state.currentRecord.accessRightCollection && state.currentRecord.accessRightCollection.items) {
            let accessRights = state.currentRecord.accessRightCollection.items;
            let rights = {};
            for (let a of accessRights) {
                let pathItems = a.resourcePath.split(/([?#])/);
                let path = pathItems.shift();
                let query = null;
                let hash = null;
                if (pathItems.length > 1 && pathItems[0] === "?") {
                    pathItems.shift();
                    query = queryParse(pathItems.shift());
                }
                if (pathItems.length > 1 && pathItems[0] === "#") {
                    pathItems.shift();
                    hash = pathItems.shift();
                }
                let methods = a.resourceMethods.split(" ");
                for (let m of methods) {
                    if (!rights[m]) rights[m] = {};
                    if (!rights[m][path]) rights[m][path] = [];
                    rights[m][path].push({query: query, hash: hash});
                }
            }
            return rights;
        }
        return null;
    }

    calcLinksRights(state) {
        if (!state) state = this.state;
        let links = [];
        if (state.links) {
            // getCollection links:
            links = links.concat(state.links);
        }
        if (state.currentRecord && state.currentRecord.link) links = links.concat(state.currentRecord.link);

        let rights = {};
        for (let l of links) {
            let method = "GET";
            switch (l["@rel"]) {
                case "add":
                    method = "POST";
                    break;
                case "remove":
                    method = "DELETE";
                    break;
                case "update":
                    method = "PUT";
                    break;
            }
            if (l["@href"]) {
                let urlObj = url.parse(l["@href"]);
                let path = urlObj.pathname;
                if (path.startsWith(CONFIG.apiRightUrl)) {
                    path = path.substring(CONFIG.apiRightUrl.length);
                    let query = queryParse(urlObj.query);
                    let hash = urlObj.hash;
                    if (hash) hash = hash.substring(1);
                    if (!rights[method]) rights[method] = {};
                    if (!rights[method][path]) rights[method][path] = [];
                    rights[method][path].push({query: query, hash: hash});
                }
            }
        }
        return rights;
    }

    /**
     * @public
     * @returns {*}
     */
    getToken() {
        if (this.state.currentRecord) return this.state.currentRecord.token;
        return null;
    }

    /**
     * @public
     * @returns {boolean}
     */
    isAuthenticated() {
        return !!(this.state.currentRecord && this.state.currentRecord.token);
    }

    passwordChangeRequired() {
        return this.state.currentRecord.status === "L";
    }

    /**
     * Method for verifying access to UI path and action
     * @param path UI full path (starting with "/ui/...")
     * @returns {boolean} Returns true if access is authorized
     */
    isAuthorized(path) {
        if (!path) return [{query: null, hash: null}];

        let rights = this.state.rights;
        if (!rights) rights = this.calcRights(this.state);
        _.defaults(rights, this.calcLinksRights(this.state));
        if (rights == null) return null;

        // Detect resource from path:

        let method = "GET";
        let splittedPath = path.split(" ");
        if (splittedPath.length > 1) {
            method = splittedPath[0];
            path = splittedPath[1];
        }

        // Remove "/ui" prefix, if present:
        if (!path.startsWith("/")) path = "/" + path;
        let pathStart = CONFIG.baseUrl;
        if (!pathStart.endsWith("/")) pathStart += "/";
        pathStart += "ui/";
        if (path.startsWith(pathStart)) {
            let pathInfo = path.substring(pathStart.length).split("/");
            let resource = pathInfo.shift();
            let field = pathInfo.join("/");
            let navSchema = SchemaRegistry.getNavigationSchemaObject(resource);
            let access = navSchema.field(field).getAccess();
            if (access) {
                path = access;
                let splittedPath = path.split(" ");
                if (splittedPath.length > 1) {
                    method = splittedPath[0];
                    path = splittedPath[1];
                }
            }
        }

        let action = null;
        if (path.indexOf("#") >= 0) {
            let idx = path.indexOf("#");
            action = path.substring(idx+1);
            path = path.substring(0, idx);
        }

        let query = null;
        if (path.indexOf("?") >= 0) {
            // Remove query:
            let idx = path.indexOf("?");
            query = queryParse(path.substring(idx+1));
            path = path.substring(0, idx);
        }

        if (!rights[method]) return null;

        // if (action) path += "#" + action;
        let pathRights = rights[method][path];
        if (!pathRights) {
            // Find if we have wildcard match:
            for (let p of _.keys(rights[method])) {
                if (p.endsWith("*") && path.startsWith(p.substring(0, p.length-1))) {
                    pathRights = rights[method][p];
                    break;
                }
            }
        }
        if (!pathRights) return null;

        for (let i of pathRights) {
            if (i.hash == action &&
                    (
                        query == null ||
                        i.query == null ||
                            _.isMatch(
                                _.mapObject(i.query, function () {return null;}),
                                _.mapObject(query, function () {return null;}))
                    ) ) return i;
        }
        return null;
    }

    filterAuthorizedActions(path, actions) {
        if (!actions) return actions;
        return _.filter(actions, function (a) {
            return !a.access || this.isAuthorized(a.access);
        }.bind(this));
    }
}

/**
 *
 * @type {AltJS.AltStore<SessionState>|SessionStore}
 */
let sessionStore = alt.createStore(SessionStore, 'SessionStore');
export default sessionStore;
